import Song from '../../Classes/Song.js';
import { paletteList } from '../../constants/colors';

export function playlistCompareByCreated(play1, play2) {
    if (play1.dateFollowed > play2.dateFollowed) {
        return 1;
    } else if (play1.dateFollowed === play2.dateFollowed) {
        return 0;
    } else {
        return -1;
    }
}

/** Returns a promise that resolves with a playlist object of the given ID
 *
 * @param {string} playlist_id - id of playlist
 * @returns promise that resolves with a playlist document snapshot of the given ID
 */
export async function getPlaylist(playlist_id) {
    const playlist = await this.db.collection('playlists').doc(playlist_id).get();
    if (!playlist.exists) throw this.NotFoundError;
    return playlist;
}

export async function getFollowedPlaylists() {
    const uid = this.auth.currentUser.uid;
    const playlistFollowedRef = this.db.collection('playlistsFollowed');
    const querySnapshot = await playlistFollowedRef.where('follower', '==', uid).orderBy('dateFollowed', 'desc').get();
    return await this.playlistsOfPlaylistsFollowed(querySnapshot);
}

export async function getAuthoredPlaylists(uid) {
    const playlistsRef = this.db.collection('playlists');
    const querySnapshot = await playlistsRef.where('author', '==', uid).orderBy('created', 'desc').get();
    return querySnapshot.docs.map((docSnap) => {
        var data = docSnap.data();
        data.playlistId = docSnap.id;
        return data;
    });
}

export async function playlistsOfPlaylistsFollowed(querySnapshot) {
    if (querySnapshot.empty) return [];
    const playlistPromises = querySnapshot.docs.map(async (playlistFollowedSnap) => {
        const playlistId = playlistFollowedSnap.get('playlistId');
        const playlistSnap = await this.getPlaylist(playlistId);
        var data = playlistSnap.data();
        data.playlistId = playlistSnap.id;
        return data;
    });
    const playlists = await Promise.all(playlistPromises);
    return playlists.sort(this.playlistCompareByCreated);
}

/**
 * Updates the order of an existing playlist songs.
 * @param {string} playlist_id
 * @param {Array} songs
 */
export async function updatePlaylistSongsOrder(playlist_id, songs) {
    var playlistRef = this.db.collection('playlists').doc(playlist_id);

    const updatedSongArr = [];
    for (var i = 0; i < songs.length; i++) {
        var songRef = this.db.collection('songs').doc(songs[i].id);
        updatedSongArr.push(songRef);
    }

    await playlistRef.update({
        songArr: updatedSongArr,
    });
}

/** Returns a promise that resolves when all the songs of the playlist are successfully retrieved
 *
 * @param {DocumentSnapshot} playlist
 * @returns an array of song document snapshots
 */
export async function getPlaylistSongs(playlist) {
    const songRefs = playlist.data().songArr;
    if (songRefs.length === 0) return [];

    const promiseArr = await Promise.all(
        songRefs.map((item, key) => {
            return item.withConverter(Song.songConverter).get();
        }),
    );

    const resArr = promiseArr.map((item, key) => {
        const tempP = item.data();
        tempP.id = item.id;
        return tempP;
    });

    return resArr;
}

/** Returns a promise that resolves when all the songs of the playlist are successfully retrieved.
 * same as above function, but takes in the playlist id instead
 * @param {playlistid} playlist
 * @returns an array of song document snapshots
 */
export async function getPlaylistSongsById(playlistId) {
    const playlistData = await this.db.collection('playlists').doc(playlistId).get();
    var songRefs = playlistData.data().songArr;
    if (songRefs.length === 0) return [];

    const promiseArr = await Promise.all(
        songRefs.map((item, key) => {
            return item.withConverter(Song.songConverter).get();
        }),
    );

    const resArr = promiseArr.map((item, key) => {
        const tempP = item.data();
        tempP.id = item.id;
        return tempP;
    });

    return resArr;
}

/** Create a new playlist document with the given name, given description, and
 * empty song array. The document ID is auto-generated. Returns the document reference of
 * the playlist.
 *
 * @param {string} name - name of playlist
 * @param {string} description - description of playlist
 * @returns {Promise<DocumentReference>}
 */
export async function addPlaylist(name, description) {
    const playlistsRef = this.db.collection('playlists');
    const followingRef = this.db.collection('playlistsFollowed');
    const playlistRef = await playlistsRef.add({
        name: name,
        description: description,
        songArr: [],
        created: this.firebase.firestore.FieldValue.serverTimestamp(),
        author: this.auth.currentUser.uid,
        color: paletteList[Math.floor(Math.random() * paletteList.length)],
        deleting: false,
    });
    const playlistId = playlistRef.id;
    await followingRef.add({
        follower: this.auth.currentUser.uid,
        author: this.auth.currentUser.uid,
        playlistId: playlistId,
        dateFollowed: this.firebase.firestore.FieldValue.serverTimestamp(),
    });

    //Log in event 'playlist_created' [Firebase Analytics]
    this.analytics.logEvent('playlist_created');
    return playlistRef;
}

/** Deletes the playlist document given playlist id and user id.
 *
 * @param {string} playlist_id - id of playlist
 * @return {Promise<void>} a promise
 */
export async function removePlaylist(playlist_id) {
    const playlistRef = this.db.collection('playlists').doc(playlist_id);
    await playlistRef.delete();
    return;
}

/** Adds the given song reference to songArr field in a playlist document. It
 * adds only if the song reference doesn't exist in the array already.
 *
 * @param {string} playlist_id - id of playlist
 * @param {string} songId - id of song
 * @returns a promise that resolves with true if the update is sucessfull.
 */
export async function addPlaylistSong(playlist_id, songId) {
    var playlistRef = this.db.collection('playlists').doc(playlist_id);
    var songRef = this.db.collection('songs').doc(songId);
    await playlistRef.update({
        songArr: this.firebase.firestore.FieldValue.arrayUnion(songRef),
    });
    return true;
}

/** Removes the given song reference from songArr field in a playlist document.
 *
 * @param {string} playlist_id - id of playlist
 * @param {string} songId - id of song
 * @returns a promise
 */
export async function removePlaylistSong(playlist_id, songId) {
    var playlistRef = this.db.collection('playlists').doc(playlist_id);
    var songRef = this.db.collection('songs').doc(songId);
    await playlistRef.update({
        songArr: this.firebase.firestore.FieldValue.arrayRemove(songRef),
    });
    return;
}

/**
 * Returns a promise that resolves when the playlist name and description is updated.
 */
export async function editPlaylist(playlist_id, name, desc, hex) {
    var playlistRef = this.db.collection('playlists').doc(playlist_id);
    await playlistRef.update({
        name: name,
        description: desc,
        color: hex,
    });
    return;
}

/**
  same as above function, but only returns the array of playlist names as 
  opposed to the playlist name and the doc ref
   * @returns a promise of an array of playlist names
  */
export async function getAuthoredPlaylistNameArray() {
    const authoredPlaylists = await this.getAuthoredPlaylists(this.props.firebase.auth.currentUser.uid);
    console.log(this.props.firebase.auth.currentUser.uid);
    var playlistNameArray = [];
    authoredPlaylists.forEach((playlist) => {
        playlistNameArray.push(playlist.name);
    });
    return playlistNameArray;
}
